import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Check } from 'react-feather';
import { Badge } from '../ui/badge';
import { FeatherAlertCircle, FeatherClock, FeatherCalendar, FeatherTag } from '../ui/icons';
import FeatureTagDetailModal from '../FeatureTags/FeatureTagDetailModal';
import TaskDueDate from '../shared/TaskDueDate';
import './FeatureTagsSection.css';

const FeatureTagsSection = ({ featureTags, projectId, onTaskClick, variant, featureCompletionMap = {}, featureDelayMap = {}, allFeatureTags = [], allTasks = [] }) => {
    const isReport = variant === 'report';
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [selectedFeatureTag, setSelectedFeatureTag] = useState(null);
    const [isFeatureTagModalOpen, setIsFeatureTagModalOpen] = useState(false);

    const getStatusVariant = (status) => {
        switch (status) {
            case 'overdue':
                return 'error';
            case 'due-today':
            case 'urgent':
                return 'warning';
            case 'upcoming':
                return 'warning';
            default:
                return 'secondary';
        }
    };

    const getStatusText = (feature) => {
        if (feature.status === 'overdue') {
            return `${feature.days_overdue} day${feature.days_overdue !== 1 ? 's' : ''} overdue`;
        } else if (feature.status === 'due-today') {
            return 'Due today';
        } else if (feature.status === 'urgent') {
            return `${feature.days_until_due} day${feature.days_until_due !== 1 ? 's' : ''} left`;
        } else if (feature.status === 'upcoming') {
            return `${feature.days_until_due} day${feature.days_until_due !== 1 ? 's' : ''} left`;
        } else {
            return new Date(feature.due_date).toLocaleDateString();
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'overdue':
                return <FeatherAlertCircle size={14} />;
            case 'due-today':
            case 'urgent':
                return <FeatherClock size={14} />;
            case 'upcoming':
            case 'normal':
            default:
                return <FeatherCalendar size={14} />;
        }
    };

    if (!featureTags || featureTags.length === 0) {
        return (
            <div className="feature-tags-section">
                <div 
                    className="section-header" 
                    onClick={() => setIsCollapsed(!isCollapsed)} 
                    style={{ cursor: 'pointer' }}
                >
                    <h2>Feature Tags with Due Dates</h2>
                    <button 
                        className="stats-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsCollapsed(!isCollapsed);
                        }}
                        aria-label={isCollapsed ? "Expand" : "Collapse"}
                    >
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
                <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="empty-state">
                        <p>No feature tags with due dates found for this project.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`feature-tags-section ${isReport ? 'feature-tags-section-report' : ''}`}>
            <div 
                className="section-header" 
                onClick={() => setIsCollapsed(!isCollapsed)} 
                style={{ cursor: 'pointer' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <h2>Feature Tags with Due Dates</h2>
                    <Badge variant="secondary">
                        {featureTags.length} {featureTags.length === 1 ? 'feature tag' : 'feature tags'}
                    </Badge>
                </div>
                <button 
                    className="stats-toggle-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsCollapsed(!isCollapsed);
                    }}
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                >
                    {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </button>
            </div>
            <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="feature-tags-grid">
                {featureTags.map((feature) => {
                    const isCompleted = featureCompletionMap[feature.id] === 100;
                    return (
                    <div
                        key={feature.id}
                        className={`feature-tag-card feature-tag-card--${isCompleted ? 'completed' : feature.status} ${isReport ? 'feature-tag-card-report' : ''}`}
                    >
                        <div className="feature-tag-card-header">
                            <div className="feature-tag-title-section">
                                <div
                                    className="feature-tag-color-indicator clickable-feature-tag"
                                    style={{ backgroundColor: feature.color || '#6366f1', cursor: 'pointer' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const fullFeatureTag = allFeatureTags.find(ft => ft.id === feature.id) || feature;
                                        setSelectedFeatureTag(fullFeatureTag);
                                        setIsFeatureTagModalOpen(true);
                                    }}
                                    title="Click to view feature details"
                                />
                                <div className="feature-tag-info">
                                    <h4 
                                        className="feature-tag-name clickable-feature-tag"
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const fullFeatureTag = allFeatureTags.find(ft => ft.id === feature.id) || feature;
                                            setSelectedFeatureTag(fullFeatureTag);
                                            setIsFeatureTagModalOpen(true);
                                        }}
                                        title="Click to view feature details"
                                    >
                                        {feature.name}
                                    </h4>
                                    <div className="feature-tag-meta">
                                        {featureCompletionMap[feature.id] === 100 ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
                                                <span className="feature-tag-completed-badge">
                                                    <Check size={14} />
                                                    <span>Completed</span>
                                                </span>
                                                {feature.due_date && featureDelayMap[feature.id] && (
                                                    <span className="feature-tag-delay-info">
                                                        Delayed by {featureDelayMap[feature.id]} day{featureDelayMap[feature.id] !== 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                <Badge
                                                    variant={getStatusVariant(feature.status)}
                                                    icon={getStatusIcon(feature.status)}
                                                >
                                                    {getStatusText(feature)}
                                                </Badge>
                                                <TaskDueDate dueDate={feature.due_date} />
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="feature-tag-task-count">
                                {(() => {
                                    const activeTasks = feature.tasks?.filter(task => task.status !== 'completed') || [];
                                    const activeCount = activeTasks.length;
                                    return `${activeCount} ${activeCount === 1 ? 'active task' : 'active tasks'}`;
                                })()}
                            </div>
                        </div>
                        <div className="feature-tag-tasks">
                            {(() => {
                                // Filter out completed tasks
                                const activeTasks = feature.tasks?.filter(task => task.status !== 'completed') || [];
                                
                                if (activeTasks.length > 0) {
                                    return (
                                        <ul className="feature-tag-tasks-list">
                                            {activeTasks.map((task) => (
                                                <li
                                                    key={task.id}
                                                    className={`feature-tag-task-item ${!isReport && onTaskClick ? 'feature-tag-task-item-clickable' : ''}`}
                                                    onClick={!isReport && onTaskClick ? (e) => {
                                                        e.stopPropagation();
                                                        onTaskClick({
                                                            id: task.id,
                                                            title: task.title,
                                                            display_id: task.display_id,
                                                            description: task.description,
                                                            status: task.status,
                                                            feature_tag: task.feature_tag,
                                                            created_at: task.created_at,
                                                            updated_at: task.updated_at
                                                        });
                                                    } : undefined}
                                                >
                                                    <div className="feature-tag-task-row-1">
                                                        <FeatherTag size={12} className="feature-tag-task-icon" />
                                                        <span className="feature-tag-task-title">{task.title}</span>
                                                    </div>
                                                    <div className="feature-tag-task-row-2">
                                                        {task.display_id && (
                                                            <span className="feature-tag-task-id">{task.display_id}</span>
                                                        )}
                                                        {task.assigned_to && (
                                                            <span className="feature-tag-task-assignee">
                                                                {task.assigned_to.display_name || task.assigned_to.email || 'Unknown'}
                                                            </span>
                                                        )}
                                                        <Badge
                                                            variant={
                                                                task.status === 'in-progress'
                                                                    ? 'warning'
                                                                    : 'secondary'
                                                            }
                                                        >
                                                            {task.status}
                                                        </Badge>
                                                        {task.picked && (
                                                            <Badge variant="success">
                                                                Picked
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    );
                                } else {
                                    return <div className="feature-tag-empty">No active tasks (all completed)</div>;
                                }
                            })()}
                        </div>
                    </div>
                    );
                })}
                </div>
            </div>

            {/* Feature Tag Detail Modal */}
            {selectedFeatureTag && (
                <FeatureTagDetailModal
                    tag={selectedFeatureTag}
                    isOpen={isFeatureTagModalOpen}
                    onClose={() => {
                        setIsFeatureTagModalOpen(false);
                        setSelectedFeatureTag(null);
                    }}
                    tasks={allTasks.filter(task => task.feature_tag?.id === selectedFeatureTag.id)}
                    users={[]}
                />
            )}
        </div>
    );
};

export default FeatureTagsSection;

